import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonCustomExceptions = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Designing Custom Exceptions</h1>
                <p className="subtitle">Building semantic error hierarchies for professional libraries. From Inheritance to Chaining.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Imagine driving a car. The dashboard lights up.
                    </p>
                    <ul>
                        <li><strong>Standard Error (`Exception`):</strong> A generic red light just says "ERROR". Is it the engine? The tire? The door? You don't know. You have to stop everything.</li>
                        <li><strong>Custom Exception (`EngineOverheatError`):</strong> A specific symbol lights up. You know exacty what's wrong. You can decide if you need to stop immediately or if you can drive to the nearest garage.</li>
                        <li><strong>The Goal:</strong> Custom exceptions give the <em>caller</em> of your code the power to make specific decisions based on exactly what failed.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The Syntax: Inheriting from Exception</h2>
                <p>
                    A custom exception is just a normal class that inherits from `Exception`. You can add method, properties, and custom logic to it.
                </p>
                <CodeBlock language="python" code={`class NegativeBalanceError(Exception):
    """Raised when a withdrawal is larger than the balance."""
    
    def __init__(self, balance, amount):
        self.balance = balance
        self.amount = amount
        # Pass a nice message to the parent class
        super().__init__(f"Cannot withdraw {amount}. Balance is only {balance}.")

# Usage
try:
    raise NegativeBalanceError(100, 500)
except NegativeBalanceError as e:
    print(e)             # Cannot withdraw 500. Balance is only 100.
    print(e.balance)     # 100 (We can access the data programmatically!)`} />
            </div>

            <div className="content-section">
                <h2>3. Best Practice: The Library Base Exception</h2>
                <p>
                    When writing a library (e.g., `requests`, `pandas`), you should never let your internal implementation details leak out.
                    <strong>Always create a base exception class for your project.</strong>
                </p>
                <CodeBlock language="python" code={`# 1. The Base Class
class FileFusionError(Exception):
    """Base class for all exceptions in this module."""
    pass

# 2. Specific Errors inherit from the Base
class ConnectionError(FileFusionError):
    pass

class AuthError(FileFusionError):
    pass

# 3. Usage for the User
try:
    file_fusion.connect()
except FileFusionError:
    # Catches ConnectionError, AuthError, and any future errors
    # WITHOUT catching ValueError (which might be a bug in their own code)
    print("Something went wrong with FileFusion!")`} />
                <ProTip><p><strong>Anti-Pattern:</strong> Never inherit from `BaseException`. It skips the normal exception hierarchy and might prevent users from stopping your code with Ctrl+C.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>4. Deep Dive: Exception Chaining (`raise from`)</h2>
                <p>
                    Sometimes you catch an error (like `KeyError`) but want to re-raise it as a semantic error (like `ConfigError`).
                    You must preserve the original traceback so debugging isn't impossible.
                </p>
                <CodeBlock language="python" code={`class ConfigError(Exception):
    pass

def load_config(key):
    try:
        data = {"host": "localhost"}
        return data[key]
    except KeyError as e:
        #  Bad: raise ConfigError("Missing Key") -&gt; Loses the original error
        
        # Good: Chain the exception
        raise ConfigError(f"Configuration missing: {key}") from e

# Traceback will show:
# KeyError: 'port'
# The above exception was the direct cause of the following exception:
# ConfigError: Configuration missing: port`} />
                <p>
                    The `from e` syntax attaches the original exception to the `__cause__` attribute of the new exception. Similary, explicit suppression uses `from None`.
                </p>
            </div>

            <div className="content-section">
                <h2>5. Attaching Payload and State</h2>
                <p>
                    Exceptions are objects. Use this!
                    Instead of parsing error string messages (flaky), attach structured data to the exception instance.
                </p>
                <CodeBlock language="python" code={`class APILimitError(Exception):
    def __init__(self, limit, reset_time):
        self.limit = limit
        self.reset_time = reset_time # Unix timestamp
        super().__init__(f"Rate limit of {limit} exceeded.")

try:
    call_api()
except APILimitError as e:
    import time
    wait_time = e.reset_time - time.time()
    print(f"Sleeping for {wait_time} seconds...")
    time.sleep(wait_time)`} />
            </div>

            <div className="content-section">
                <h2>6. When NOT to use Custom Exceptions</h2>
                <p>
                    Don't reinvent the wheel. If a built-in exception fits, use it.
                </p>
                <ul className="premium-list">
                    <li>If a function gets the wrong argument type? Use `TypeError`.</li>
                    <li>If the value is invalid (e.g., -1 for age)? Use `ValueError`.</li>
                    <li>If a file is missing? Use `FileNotFoundError`.</li>
                </ul>
                <p>
                    Only create custom exceptions when the <strong>caller needs to distinguish</strong> this specific error from others programmatically.
                </p>
            </div>

        </div>
    );
};

export default PythonCustomExceptions;


